import React, { useMemo } from "react";
import {
  Droplets, HeartPulse, Building2, Waves, Thermometer,
  Layers, ChevronRight, AlertTriangle,
} from "lucide-react";
import {
  assessServiceStress, serviceStressSummary, SERVICE_ASSUMPTIONS,
} from "../services/serviceStress";
import { correlateHazards, HAZARD_ASSUMPTIONS } from "../services/multiHazard";
import { assessMobilityChain } from "../services/mobilityChain";

/* §10 service stress and §9 multi-hazard correlation.
 *
 * Deliberately one panel: the two sections answer the same commander's
 * question from opposite directions. §10 says which single facility runs out
 * and when; §9 says which combination of pressures is worse than any of its
 * parts. Splitting them across the interface would let an operator read one
 * and miss the other, which is the exact failure the brief describes.
 */
export default function ServiceHazardPanel({ lang = "en", live, twin, onFocus }) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";

  const stress = useMemo(() => assessServiceStress(live.state), [live.state]);
  const summary = useMemo(() => serviceStressSummary(stress), [stress]);
  /* The correlator is given the other engines' output. Without it, four
   * of the sixteen hazards — traffic, utility, route blockage and comms —
   * cannot be derived and report as unmonitored, which understates the
   * platform and overstates the blind spots. */
  const hazard = useMemo(() => correlateHazards(live.state, {
    twin,
    serviceStress: stress,
    mobility: twin ? assessMobilityChain(live.state, { twin }) : null,
  }), [live.state, twin, stress]);

  const ICON = { toilets: Waves, water: Droplets, medical: HeartPulse, shelter: Building2 };

  /* Only the rows worth a commander's attention. Everything nominal is
   * counted, not listed — a scroll of green rows hides the one red one. */
  const notable = stress.filter((r) => r.state !== "ok").slice(0, 6);
  const okCount = stress.length - stress.filter((r) => r.state !== "ok").length;

  return (
    <div className="border-t border-slate-200">
      {/* ── §10 Service stress ─────────────────────────────────────── */}
      <section className="px-3 py-3">
        <div className="mb-2 flex items-center gap-1.5">
          <Droplets size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "सेवा ताण अंदाज" : "Service Stress Forecast"}
          </span>
          <span className="ml-auto rounded border border-fuchsia-300 bg-fuchsia-100 px-1.5 py-0.5 text-[8px] font-black text-fuchsia-800">
            SIM
          </span>
        </div>

        {/* The headline sentence the brief asked for, not a gauge. */}
        {summary.soonest ? (
          <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5">
            <div className="text-[10px] font-bold leading-snug text-amber-900">
              {summary.soonest.narrative}
            </div>
          </div>
        ) : summary.failed.length || summary.stressed.length ? (
          <div className="mb-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[10px] font-bold leading-snug text-red-900">
            {(summary.failed[0] ?? summary.stressed[0]).narrative}
          </div>
        ) : (
          <div className="mb-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[10px] leading-snug text-emerald-900">
            {lang === "mr"
              ? "पुढील 2 तासांत कोणतीही सेवा मर्यादा ओलांडण्याचा अंदाज नाही."
              : "No service is predicted to cross its operational threshold within the next two hours."}
          </div>
        )}

        {notable.map((r) => {
          const Icon = ICON[r.service] ?? Droplets;
          return (
            <button
              key={`${r.sectorId}-${r.service}`}
              type="button"
              onClick={() => onFocus({ coord: r.centre, zoom: 14.5 })}
              className={`mb-1 flex w-full items-center gap-1.5 rounded border px-1.5 py-1 text-left ${
                r.state === "failed" ? "border-red-300 bg-red-50"
                : r.state === "stressed" ? "border-orange-300 bg-orange-50"
                : "border-amber-200 bg-amber-50"
              }`}
            >
              <Icon size={11} className="flex-shrink-0 text-slate-500" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[10px] font-bold text-slate-800">
                  {L(r.sectorName)} · {L(r.label)}
                </div>
                <div className="text-[9px] tabular-nums text-slate-600">
                  {r.utilisationPct}% {lang === "mr" ? "वापर" : "used"} · {r.units}{" "}
                  {lang === "mr" ? "युनिट" : "units"}
                  {r.netPerMin > 0 && ` · +${r.netPerMin}/min`}
                </div>
              </div>
              {r.minutesToStress != null ? (
                <span className="flex-shrink-0 rounded bg-amber-200 px-1 py-0.5 text-[9px] font-black tabular-nums text-amber-900">
                  T−{r.minutesToStress}m
                </span>
              ) : (
                <span className="flex-shrink-0 rounded bg-red-200 px-1 py-0.5 text-[9px] font-black text-red-900">
                  {lang === "mr" ? "आता" : "NOW"}
                </span>
              )}
            </button>
          );
        })}

        <div className="mt-1 text-[9px] text-slate-400">
          {okCount} {lang === "mr" ? "सेवा-क्षेत्र संयोजने क्षमतेत" : "service/sector pairs within capacity"}
        </div>

        <Method label={lang === "mr" ? "गृहीतके" : "Method"} items={SERVICE_ASSUMPTIONS} />
      </section>

      {/* ── §9 Multi-hazard correlation ────────────────────────────── */}
      <section className="border-t border-slate-200 px-3 py-3">
        <div className="mb-2 flex items-center gap-1.5">
          <Layers size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "बहु-धोका सहसंबंध" : "Multi-Hazard Correlation"}
          </span>
          <span className="ml-auto flex items-center gap-0.5 text-[9px] font-bold tabular-nums text-slate-500">
            <Thermometer size={9} />
            {hazard.environment.tempC}° · {hazard.environment.humidity}%
          </span>
        </div>

        <p className="mb-2 text-[9px] leading-snug text-slate-500">
          {lang === "mr"
            ? "एकत्रित धोके वेगळ्या धोक्यांपेक्षा वेगळे चित्र तयार करतात."
            : "Hazards that occur together are worse than the sum of the gauges. The amplification figure is how far the correlated picture sits above the worst single reading."}
        </p>

        {hazard.sectors.slice(0, 3).map((s) => (
          <div
            key={s.id}
            className={`mb-1.5 rounded border p-1.5 ${
              s.level === "severe" ? "border-red-300 bg-red-50"
              : s.level === "elevated" ? "border-orange-300 bg-orange-50"
              : s.level === "watch" ? "border-amber-200 bg-amber-50"
              : "border-slate-200 bg-white"
            }`}
          >
            <button
              type="button"
              onClick={() => onFocus({ coord: s.centre, zoom: 14.5 })}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <span className="min-w-0 flex-1 truncate text-[10px] font-bold text-slate-800">
                {L(s.name)}
              </span>
              <span className="text-[11px] font-black tabular-nums text-slate-900">
                {Math.round(s.compound * 100)}
              </span>
              {s.amplification >= 0.1 && (
                <span className="rounded bg-slate-800 px-1 py-0.5 text-[8px] font-black tabular-nums text-white">
                  +{Math.round(s.amplification * 100)}
                </span>
              )}
            </button>

            {/* Component severities — so a compound score can always be
                taken apart again by the person acting on it. */}
            <div className="mt-1 flex flex-wrap gap-1">
              {s.hazards.map((h) => (
                <span
                  key={h.key}
                  title={h.reading}
                  className={`rounded border px-1 py-0.5 text-[8px] font-bold tabular-nums ${
                    h.severity == null ? "border-slate-200 bg-slate-100 text-slate-400"
                    : h.severity >= 0.6 ? "border-red-300 bg-white text-red-700"
                    : h.severity >= 0.3 ? "border-amber-300 bg-white text-amber-700"
                    : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  {L(h.label)} {h.severity == null ? "—" : Math.round(h.severity * 100)}
                </span>
              ))}
            </div>

            {s.interactions.length > 0 && (
              <div className="mt-1 border-t border-black/5 pt-1">
                <div className="flex items-start gap-1">
                  <AlertTriangle size={9} className="mt-0.5 flex-shrink-0 text-slate-500" />
                  <span className="text-[9px] leading-snug text-slate-700">
                    {s.interactions[0].why}
                  </span>
                </div>
              </div>
            )}

            {s.unknown.length > 0 && (
              <div className="mt-1 text-[8px] italic text-slate-400">
                {lang === "mr" ? "अज्ञात" : "unknown"}:{" "}
                {s.unknown.map((u) => u.key).join(", ")}
              </div>
            )}
          </div>
        ))}

        {hazard.amplified.length === 0 && (
          <p className="text-[9px] italic text-slate-400">
            {lang === "mr"
              ? "सध्या कोणतेही धोके एकत्र वाढत नाहीत."
              : "No hazard pair is currently compounding — every sector's risk is explained by its worst single reading."}
          </p>
        )}

        <Method label={lang === "mr" ? "गृहीतके" : "Method"} items={HAZARD_ASSUMPTIONS} />
      </section>
    </div>
  );
}

const Method = ({ label, items }) => (
  <details className="mt-2">
    <summary className="flex cursor-pointer items-center gap-0.5 text-[9px] font-bold text-slate-400 hover:text-slate-600">
      <ChevronRight size={8} />
      {label}
    </summary>
    <ul className="mt-1 space-y-0.5">
      {items.map((a) => (
        <li key={a} className="text-[9px] leading-snug text-slate-500">· {a}</li>
      ))}
    </ul>
  </details>
);
